import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
	Box,
	Card,
	CardContent,
	TextField,
	Button,
	Typography,
	InputAdornment,
	IconButton,
	Alert,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { motion } from "framer-motion";
import { loginSuccess } from "../store/slices/authSlice";
import { useSnackbar } from "notistack";
import authService from "../services/authService";
import { ThemeToggle } from "../components/ThemeMode";

export default function Login() {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState("");
	const [sessionExpired, setSessionExpired] = useState(false);
	const [loading, setLoading] = useState(false);

	// Forgot-password dialog state
	const [forgotOpen, setForgotOpen] = useState(false);
	const [forgotEmail, setForgotEmail] = useState("");
	const [forgotLoading, setForgotLoading] = useState(false);
	const [forgotSent, setForgotSent] = useState(false);
	const [forgotError, setForgotError] = useState("");

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { enqueueSnackbar } = useSnackbar();
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		if (searchParams.get("expired") === "true") {
			setSessionExpired(true);
			setSearchParams({}, { replace: true });
		}
		if (searchParams.get("suspended") === "true") {
			setError("Your organisation's access is suspended. Please contact Saarka.");
			setSearchParams({}, { replace: true });
		}
	}, [searchParams, setSearchParams]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setLoading(true);

		try {
			const response = await authService.login(email, password);
			dispatch(
				loginSuccess({
					user: response.user,
					token: response.accessToken,
				}),
			);
			enqueueSnackbar("Welcome back!", { variant: "success" });
			navigate(response.user?.role === "PLATFORM" ? "/platform" : "/dashboard");
		} catch (err) {
			const errorMessage =
				err.response?.data?.message || "Invalid email or password";
			setError(errorMessage);
			enqueueSnackbar(errorMessage, { variant: "error" });
		} finally {
			setLoading(false);
		}
	};

	const handleForgotOpen = () => {
		setForgotEmail(email); // pre-fill with whatever is already typed
		setForgotSent(false);
		setForgotError("");
		setForgotOpen(true);
	};

	const handleForgotClose = () => {
		setForgotOpen(false);
	};

	const handleForgotSubmit = async (e) => {
		e.preventDefault();
		if (!forgotEmail.trim()) {
			setForgotError("Please enter your email address.");
			return;
		}
		setForgotError("");
		setForgotLoading(true);
		try {
			await authService.forgotPassword(forgotEmail.trim());
			setForgotSent(true);
		} catch {
			setForgotError("Something went wrong. Please try again.");
		} finally {
			setForgotLoading(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: (theme) =>
					theme.palette.mode === "dark"
						? "radial-gradient(900px circle at 20% 0%, rgba(99, 102, 241, 0.22), transparent 60%), radial-gradient(700px circle at 90% 100%, rgba(8, 145, 178, 0.16), transparent 60%), #0b0d17"
						: "linear-gradient(180deg, #eef2ff 0%, #e0e7ff 100%)",
				position: "relative",
				overflow: "hidden",
			}}
		>
			<ThemeToggle sx={{ position: "absolute", top: 16, right: 16, zIndex: 1 }} />
			{/* Animated background elements */}
			<Box
				sx={{
					position: "absolute",
					width: "100%",
					height: "100%",
					opacity: 0.1,
					pointerEvents: "none",
					background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C%2Fg%3E%3C/svg%3E")`,
				}}
			/>

			{/* Positioned so the card stays above the pattern once the animation's transform is gone */}
			<motion.div
				style={{ position: "relative", zIndex: 1 }}
				initial={{ opacity: 0, scale: 0.9 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.5 }}
			>
				<Card
					sx={(theme) => ({
						maxWidth: 450,
						width: "90vw",
						borderRadius: 4,
						boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
						overflow: "visible",
						...(theme.palette.mode === "dark" && {
							bgcolor: "#1a1d2e",
							border: "1px solid rgba(255, 255, 255, 0.1)",
							boxShadow: "0 30px 80px -20px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(129, 140, 248, 0.12)",
						}),
					})}
				>
					<CardContent sx={{ p: 5 }}>
						<Box sx={{ textAlign: "center", mb: 4 }}>
							<Box
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 1.5,
									mb: 2,
									cursor: "pointer",
								}}
								onClick={() => navigate("/")}
							>
								<Box
									sx={{
										width: 40,
										height: 40,
										borderRadius: "8px",
										background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										flexShrink: 0,
										boxShadow: "0 2px 8px rgba(124, 58, 237, 0.35)",
									}}
								>
									<svg
										width="22"
										height="22"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
											stroke="white"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</Box>
								<Box sx={{ position: "relative", display: "inline-block" }}>
									<Typography
										variant="h5"
										fontWeight={800}
										sx={{
											background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 55%, #a855f7 100%)",
											WebkitBackgroundClip: "text",
											WebkitTextFillColor: "transparent",
											backgroundClip: "text",
											letterSpacing: "-0.02em",
											lineHeight: 1.2,
										}}
									>
										Testify
									</Typography>
									<Typography
										sx={{
											position: "absolute",
											top: -5,
											right: -2,
											fontSize: "7px",
											fontWeight: 700,
											color: "#7c3aed",
											letterSpacing: "0.07em",
											textTransform: "uppercase",
											lineHeight: 1,
											opacity: 0.85,
										}}
									>
										SAARKA's
									</Typography>
								</Box>
							</Box>
							<Typography variant="h4" fontWeight={700} gutterBottom>
								Welcome back
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Sign in to your Testify account
							</Typography>
						</Box>

						{sessionExpired && (
							<Alert
								severity="warning"
								sx={{ mb: 3, borderRadius: 2 }}
								onClose={() => setSessionExpired(false)}
							>
								Your session has expired. Please log in again.
							</Alert>
						)}

						{error && (
							<Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
								{error}
							</Alert>
						)}

						<Box component="form" onSubmit={handleSubmit} noValidate>
							<TextField
								margin="normal"
								required
								fullWidth
								id="email"
								label="Email Address"
								name="email"
								autoComplete="email"
								autoFocus
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Email color="action" />
										</InputAdornment>
									),
								}}
							/>
							<TextField
								margin="normal"
								required
								fullWidth
								name="password"
								label="Password"
								type={showPassword ? "text" : "password"}
								id="password"
								autoComplete="current-password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Lock color="action" />
										</InputAdornment>
									),
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={() => setShowPassword(!showPassword)}
												edge="end"
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>

							<Button
								type="submit"
								fullWidth
								variant="contained"
								size="large"
								disabled={loading}
								sx={{
									mt: 3,
									mb: 2,
									py: 1.5,
									background: "#4f46e5",
									"&:hover": { background: "#3730a3" },
								}}
							>
								{loading ? "Signing in..." : "Sign In"}
							</Button>

							<Box sx={{ mt: 1, textAlign: "center" }}>
								<Button
									variant="text"
									size="small"
									onClick={handleForgotOpen}
									sx={{ color: "primary.main", textTransform: "none" }}
								>
									Forgot password?
								</Button>
							</Box>
						</Box>
					</CardContent>
				</Card>
			</motion.div>

			{/* Forgot Password Dialog */}
			<Dialog
				open={forgotOpen}
				onClose={handleForgotClose}
				maxWidth="xs"
				fullWidth
				PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
			>
				<DialogTitle sx={{ fontWeight: 700 }}>Reset your password</DialogTitle>
				<DialogContent>
					{forgotSent ? (
						<Alert severity="success" sx={{ mt: 1 }}>
							If an account exists for that email, a reset link has been sent.
							Check your inbox (and spam folder).
						</Alert>
					) : (
						<Box component="form" id="forgot-form" onSubmit={handleForgotSubmit}>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
								Enter your account email and we'll send you a link to reset your
								password.
							</Typography>
							{forgotError && (
								<Alert severity="error" sx={{ mb: 2 }}>
									{forgotError}
								</Alert>
							)}
							<TextField
								autoFocus
								fullWidth
								label="Email Address"
								type="email"
								value={forgotEmail}
								onChange={(e) => setForgotEmail(e.target.value)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Email color="action" />
										</InputAdornment>
									),
								}}
							/>
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={handleForgotClose} sx={{ color: "text.secondary" }}>
						{forgotSent ? "Close" : "Cancel"}
					</Button>
					{!forgotSent && (
						<Button
							type="submit"
							form="forgot-form"
							variant="contained"
							disabled={forgotLoading}
							sx={{ background: "#4f46e5", "&:hover": { background: "#3730a3" } }}
						>
							{forgotLoading ? "Sending..." : "Send Reset Link"}
						</Button>
					)}
				</DialogActions>
			</Dialog>
		</Box>
	);
}
